import { Vector2, Vector3 } from "three";
import { PrecisionUtils } from "../../infra3d";
import { MeasureHandler, STREAMTYPE, MeasurePoint } from "./internal";
/**
 * Measure point representation
 * @class MeasurePoint
 * @extends Obj
 * @constructor
 */
export class MonoplotMeasurePoint extends MeasurePoint {
    constructor(geoframe, mouseX, mouseY, maxdist, disp) {
        super();
        this.offsetEasting = 0;
        this.offsetNorthing = 0;
        this.offsetHeight = 0;
        this.disparity = 0.0;
        /**
         * Bildkoordinate x'
         * @property {number} imageX
         */
        this.imageX = 0.0;
        /**
         * Bildkoordinate y
         * @property {number} imageY
         */
        this.imageY = 0.0; // Bildkoordinate y'.
        this.color = null;
        this.disparityInPixel = -1;
        this.disparityInmm = -1;
        this.measureInfo = null;
        this._sensorid = 0;
        this.geoframe = geoframe;
        this.mouseX = mouseX;
        this.mouseY = mouseY;
        this.framenumber = geoframe.getFramenumber();
        this.offsetEasting = geoframe.easting;
        this.offsetNorthing = geoframe.northing;
        this.offsetHeight = geoframe.height;
        this.Omega = (geoframe.omega * Math.PI) / 180;
        this.Phi = (geoframe.phi * Math.PI) / 180;
        this.Kappa = (geoframe.kappa * Math.PI) / 180;
        this.disparity = disp;
        this.maxdist = maxdist;
        this.calcCoordinates(mouseX, mouseY);
        this.checkCoordinates();
    }
    isValid() {
        if (!isFinite(this.Easting) ||
            isNaN(this.Easting) ||
            !isFinite(this.Northing) ||
            isNaN(this.Northing) ||
            !isFinite(this.Height) ||
            isNaN(this.Height)) {
            return false;
        }
        return true;
    }
    /**
     * Calculates the coordinates out of the mouse position
     *
     * @param {number} mouseX - from canvas coordinates canvas left-top is origin !
     * @param {number} mouseY - from canvas coordinates canvas left-top is origin !
     */
    calcCoordinates(mouseX, mouseY) {
        const mh = this.geoframe.scene.getMeasureHandler();
        const measureinfo = this.geoframe.getMeasureInfo();
        const mapcoorobj = mh.mouseToMap(mouseX, mouseY, this.geoframe);
        const mc = new Vector2(mapcoorobj[0].coords.x, mapcoorobj[0].coords.y);
        const senscoor = mh.mapToSensor(mc, this.geoframe);
        const imgcoor = mh.sensorToImage(senscoor, this.geoframe);
        this.imageX = imgcoor.x;
        this.imageY = imgcoor.y;
        this._sensorid = mapcoorobj[0].sensorid;
        let disp_result;
        if (measureinfo.streamtype == STREAMTYPE.CUBEMAP) {
            disp_result = mh.mapToDisparity(mapcoorobj[0].coords, mapcoorobj[0].cubefaceindex);
        }
        else {
            disp_result = mh.mapToDisparity(mapcoorobj[0].coords, this._sensorid);
        }
        this.disparity = disp_result[0];
        this.disparityInmm = this.disparity * measureinfo.depthmapmeta.pixsize * 0.001;
        this.quality = disp_result[2];
        //calculate the depth in m
        const depth = disp_result[1];
        const modelcoor = mh.imageToModel(imgcoor, depth, null, this.geoframe);
        let framecoordinates;
        if (measureinfo.streamtype == STREAMTYPE.CUBEMAP) {
            framecoordinates = mh.modelToFrame(modelcoor, this._sensorid, this.geoframe, mapcoorobj[0].cubefaceorientation);
        }
        else {
            framecoordinates = mh.modelToFrame(modelcoor, this._sensorid, this.geoframe);
        }
        const worldcoor = mh.frameToWorld(framecoordinates, this.geoframe);
        this.Easting = worldcoor.x;
        this.Northing = worldcoor.y;
        this.Height = worldcoor.z;
        this._modelEasting = modelcoor.x;
        this._modelNorthing = modelcoor.y;
        this._modelHeight = modelcoor.z;
        this.frameX = framecoordinates.x;
        this.frameY = framecoordinates.y;
        this.frameZ = framecoordinates.z;
        this._calcPrecision();
    }
    _calcPrecision() {
        this._calcPrecisionLocal();
        this._calcPrecisionLocalGeoFrame();
        this._calcPrecisionLocalGlobal();
        this._calcPrecisionGlobal();
    }
    _calcPrecisionLocal() {
        const measureinfo = this.geoframe.getMeasureInfo();
        const precX = measureinfo.precDisparity * measureinfo.imagemeta.pixsize * 0.001;
        let precP = measureinfo.precDisparity * measureinfo.depthmapmeta.pixsize * 0.001;
        if (this.quality === 64) {
            precP = 2.0 * precP;
        }
        // NOTE: All units in [mm].
        const args = {
            x: this.imageX,
            y: this.imageY,
            p: this.disparityInmm,
            c: measureinfo.imagemeta.focallength,
            b: measureinfo.depthmapmeta.basis * 1000.0,
            precX: precX,
            precY: precX,
            precP: precP,
            precC: measureinfo.precFocallength,
            precB: measureinfo.precBasis,
        };
        const r = PrecisionUtils.pointLocal(args);
        // NOTE: All units in [m].
        this.precLocalX = r.x * 0.001;
        this.precLocalY = r.y * 0.001;
        this.precLocalZ = r.z * 0.001;
    }
    _calcPrecisionLocalGeoFrame() {
        const precLocal = this._precisionLocalGeoFrame();
        this.precFrameX = precLocal.x;
        this.precFrameY = precLocal.y;
        this.precFrameZ = precLocal.z;
    }
    _calcPrecisionGlobal() {
        const precLocal = this._precisionLocalGeoFrame();
        const args = {
            x: this._modelEasting, // [m]
            y: this._modelNorthing, // [m]
            z: this._modelHeight, // [m]
            omega: this.Omega, // [rad]
            phi: this.Phi, // [rad]
            kappa: this.Kappa, // [rad]
            precX: precLocal.x * 1000.0, // [mm]
            precY: precLocal.y * 1000.0, // [mm]
            precZ: precLocal.z * 1000.0, // [mm]
            precOmega: (this.geoframe.precOmega * Math.PI) / 180, // [rad]
            precPhi: (this.geoframe.precPhi * Math.PI) / 180, // [rad]
            precKappa: (this.geoframe.precOmega * Math.PI) / 180, // [rad]
            precTx: this.geoframe.precEasting * 1000.0, // [mm]
            precTy: this.geoframe.precNorthing * 1000.0, // [mm]
            precTz: this.geoframe.precHeight * 1000.0, // [mm]
        };
        const r = PrecisionUtils.pointGlobal(args);
        // NOTE: All units in [m].
        this.precGlobalEasting = r.x * 0.001;
        this.precGlobalNorthing = r.y * 0.001;
        this.precGlobalHeight = r.z * 0.001;
    }
    _calcPrecisionLocalGlobal() {
        const precLocal = this._precisionLocalGeoFrame();
        const R = MeasureHandler.getRotationMatrix((this.geoframe.omega * Math.PI) / 180, (this.geoframe.phi * Math.PI) / 180, (this.geoframe.kappa * Math.PI) / 180);
        precLocal.applyMatrix4(R);
        this.precLocalEasting = Math.abs(precLocal.x);
        this.precLocalNorthing = Math.abs(precLocal.y);
        this.precLocalHeight = Math.abs(precLocal.z);
    }
    _precisionLocalGeoFrame() {
        // NOTE: Transform local precisions to sensorarray /
        // geoframe.
        const precLocal = new Vector3(this.precLocalX, this.precLocalY, this.precLocalZ);
        const minfo = this.geoframe.getMeasureInfo();
        const sensorpos = minfo.sensors[this._sensorid].position;
        const R = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        precLocal.applyMatrix4(R);
        return precLocal;
    }
    /**
     * returns the point values as vec3 object.
     * @return {MeasurePoint}
     */
    copy() {
        const pointcopy = new MonoplotMeasurePoint(this.geoframe, this.mouseX, this.mouseY, this.maxdist);
        pointcopy.framenumber = this.framenumber;
        pointcopy["Height"] = this["Height"];
        pointcopy["Easting"] = this["Easting"];
        pointcopy["Northing"] = this["Northing"];
        return pointcopy;
    }
    /**
     * checks if the measurement is in Range
     */
    checkCoordinates() {
        this.warntext = "";
        this.warning = false;
        if (this.maxdist == null) {
            this.warning = true;
            this.warntext = "No maximal range value set !";
            return;
        }
        if (this.disparity == 0 || this.disparity == undefined) {
            this.warning = true;
            this.warntext = "Disparity value is 0 or undefined";
            return;
        }
        const d = Math.sqrt(Math.pow(this["Easting"] - this.offsetEasting, 2) +
            Math.pow(this["Northing"] - this.offsetNorthing, 2) +
            Math.pow(this["Height"] - this.offsetHeight, 2));
        this.distance = d;
        if (d > this.maxdist) {
            this.warning = true;
            this.warntext = "Position too far from projection center";
            return;
        }
        if (!this.isValid()) {
            this.warning = true;
            this.warntext = "Inavlid Values in Northing/Easting/Height";
            return;
        }
    }
}
//# sourceMappingURL=monoplotmeasurepoint.js.map